/**
 * Web browsers play AAC in M4A containers. ALAC (Apple Lossless) inside
 * .m4a only works in Safari — Chrome/Firefox show 0:00 and fail to decode.
 */

export function canPlayAlac(): boolean {
  if (typeof Audio === 'undefined') {
    return false;
  }
  const audio = new Audio();
  return !!audio.canPlayType('audio/mp4; codecs="alac"');
}

export function canPlayAacInMp4(): boolean {
  if (typeof Audio === 'undefined') {
    return false;
  }
  const audio = new Audio();
  return !!audio.canPlayType('audio/mp4; codecs="mp4a.40.2"');
}

/**
 * Sniffs the first bytes of an M4A/MP4 blob for the sample-entry fourcc.
 * ALAC files contain `alac`; AAC typically contains `mp4a`.
 */
export async function detectM4aCodec(
  blob: Blob
): Promise<'alac' | 'aac' | 'unknown'> {
  const probeSize = Math.min(blob.size, 64 * 1024);
  const bytes = new Uint8Array(await blob.slice(0, probeSize).arrayBuffer());
  const ascii = Array.from(bytes, b => String.fromCharCode(b)).join('');

  const hasAlac = ascii.includes('alac');
  const hasAac = ascii.includes('mp4a');

  if (hasAlac && !hasAac) {
    return 'alac';
  }
  if (hasAac) {
    return 'aac';
  }
  return 'unknown';
}

export function isBrowserPlayableM4a(codec: 'alac' | 'aac' | 'unknown'): boolean {
  if (codec === 'aac' || codec === 'unknown') {
    return canPlayAacInMp4() || canPlayAlac();
  }
  return canPlayAlac();
}
